import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "../../../../shared/components/Logo";
import { useActiveCatalogs } from "../../../../shared/hooks/useCatalogs";

export function Footer() {
  const { data: catalogsData, isLoading: catalogsLoading } =
    useActiveCatalogs();
  
  const currentYear = new Date().getFullYear();
  
  // Coordenadas de la empresa (Mendoza, Argentina)
  const businessLocation: [number, number] = [
    -32.87284410944235, -68.8195651711744,
  ];
  
  // URL de Google Maps con las coordenadas
  const googleMapsUrl = `https://www.google.com/maps?q=${businessLocation[0]},${businessLocation[1]}`;

  return (
    <footer className="bg-white text-gray-900 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Contenido principal */}
        <div className="py-8 sm:py-12">
          {/* Grid responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Información de la empresa */}
            <div className="space-y-4">
              <div className="flex items-center">
                <Logo width={120} height={40} />
              </div>
              <p className="hidden sm:block text-sm text-gray-600 leading-relaxed">
                Especialistas en cerámicas y materiales de construcción de alta calidad. 
                Más de 80 años de experiencia en Mendoza, desde 1944. Tercera generación 
                al servicio de tu proyecto.
              </p>
            </div>

            {/* Enlaces rápidos */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">
                Enlaces Rápidos
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  >
                    Inicio
                  </Link>
                </li>
                {catalogsLoading ? (
                  <li>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
                  </li>
                ) : catalogsData?.data?.items && catalogsData.data.items.length > 0 ? (
                  catalogsData.data.items.map((catalog) => (
                    <li key={catalog.id}>
                      <Link
                        to={`/catalog/${catalog.slug}`}
                        className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
                      >
                        {catalog.name}
                      </Link>
                    </li>
                  ))
                ) : null}
                <li>
                  <Link
                    to="/contact"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  >
                    Contacto
                  </Link>
                </li>
              </ul>
            </div>

            {/* Información de contacto */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Contacto</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <a 
                    href="tel:+5492617135406"
                    className="text-sm text-gray-600 hover:text-[#282828] transition-colors"
                  >
                    +54 9 2617 13-5406
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <a 
                    href="mailto:gassullceramica@gmail.com"
                    className="text-sm text-gray-600 hover:text-[#282828] transition-colors break-all"
                  >
                    gassullceramica@gmail.com
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-[#282828] transition-colors"
                  >
                    Lateral Oeste de Costanera Norte y, Bajada de Arrollabes
                    <span className="text-gray-500">
                      {" "}
                      (A una cuadra de la rotonda del avión)
                    </span>
                    <br />
                    M5539 Las Heras, Mendoza, Argentina
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Línea divisoria */}
        <div className="border-t border-gray-200"></div>

        {/* Copyright */}
        <div className="py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-sm text-gray-500 text-center sm:text-left">
              © {currentYear} Cerámicas Gassull. Todos los derechos reservados.
            </p>
            <div className="flex space-x-6 text-sm text-gray-500">
              <Link
                to="/privacy"
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Privacidad
              </Link>
              <Link
                to="/terms"
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Términos
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
